/* containment.js — ROOM-CONTAINMENT GUARANTEE (user mandate): a wall cabinet's
 * FOOTPRINT POLYGON must lie fully inside the room its wall borders, no matter
 * what the span clamp / snapping produced. This is the FINAL gate, not a new
 * clamp: the signed-span math remains the fast path; this catches everything
 * else (wrong-side placements at open-chain corners, forced offsets, future
 * regressions) so a cabinet can NEVER end up outside the room.
 *
 *   cabinetFootprint(p, def, walls)      → [[x,z]×4] oriented rect | null
 *   footprintViolation(p, def, walls)    → null | "outside-room"
 *
 * Policy:
 *  - Applies to wall-attached, non-island cabinets whose host wall borders at
 *    least one CLOSED room (getWallGraph(walls).rooms). Open chains have no
 *    room polygon — there the existing wall-body SAT (validation rule 1b)
 *    remains the physical guard.
 *  - The room is chosen by the cabinet's CENTER among the host wall's bordering
 *    rooms; all four corners must lie in that same room. Corners are shrunk
 *    3mm toward the center first, so byte-exact flush-in-corner contact with
 *    the room boundary stays legal (flush placement is a feature, not a bug).
 */
import { getWallGraph, pointInPolygon } from "./wallGraph";
import { cabinetWorldPos, getWallPlacementInfo, lhcRoomNormal } from "./placement";
import { effectiveThickness } from "./wallJoins";
import { sidePanelExtents } from "./sidePanelClearance";

const SHRINK_M = 0.003; // boundary-contact tolerance: flush stays valid

/** Oriented footprint rectangle of a wall cabinet (width along the wall,
 *  depth along the facing normal), from the SAME pose the renderer uses. */
export function cabinetFootprint(p, def, walls) {
  if (!p || !def || p.island || !p.wallId) return null;
  const pos = cabinetWorldPos(p, def, walls);
  if (!pos || !pos.wall) return null;
  const e = sidePanelExtents(p, def);                    // the panels stay inside the room too
  const hw = def.width / 2 + (e.left + e.right) / 2, hd = def.depth / 2;
  const dx = pos.dirX, dz = pos.dirZ, nx = pos.nx, nz = pos.nz;
  const sh = (e.right - e.left) / 2;
  const cx = pos.cx + dx * sh, cz = pos.cz + dz * sh;
  return [
    [cx - dx * hw - nx * hd, cz - dz * hw - nz * hd],
    [cx + dx * hw - nx * hd, cz + dz * hw - nz * hd],
    [cx + dx * hw + nx * hd, cz + dz * hw + nz * hd],
    [cx - dx * hw + nx * hd, cz - dz * hw + nz * hd],
  ];
}

/** null when the placement is fine (or containment does not apply);
 *  "outside-room" when any footprint corner leaves the room polygon (closed
 *  rooms) or tucks behind an adjacent wall's band at a corner (open chains). */
export function footprintViolation(p, def, walls) {
  const fp = cabinetFootprint(p, def, walls);
  if (!fp) return null;
  const rooms = (getWallGraph(walls).rooms || []).filter((r) => r.wallIds.includes(p.wallId));
  if (!rooms.length) return openChainViolation(p, fp, walls); // no closed room yet
  const cx = (fp[0][0] + fp[2][0]) / 2, cz = (fp[0][1] + fp[2][1]) / 2;
  const room = rooms.find((r) => pointInPolygon([cx, cz], r.polygon));
  if (!room) return "outside-room"; // center not in ANY room this wall borders
  for (const c of fp) {
    const q = [c[0] + (cx - c[0]) * (SHRINK_M / Math.max(1e-9, Math.hypot(cx - c[0], cz - c[1]))),
               c[1] + (cz - c[1]) * (SHRINK_M / Math.max(1e-9, Math.hypot(cx - c[0], cz - c[1])))];
    if (!pointInPolygon(q, room.polygon)) return "outside-room";
  }
  return null;
}

/** OPEN-LAYOUT half-space guard (user mandate v2: "walls are HARD boundaries —
 *  the footprint must stay entirely on the interior side of EVERY wall,
 *  regardless of whether the room is open or closed").
 *  When the host wall borders no closed room (no polygon yet), every standard
 *  straight wall constrains placement within its own run: a footprint corner
 *  that projects inside wall w2's along-extent must lie on w2's INTERIOR side
 *  (signed distance from its inner face ≥ −3mm; the grace keeps byte-flush
 *  contact legal). Crossing into or past the band ⇒ outside. Corners beyond the
 *  wall's run are unconstrained by it (that is open floor, and it also keeps
 *  convex-corner flush legal — the corner projects past the neighbour's run).
 *  This is a true geometric half-space test against ALL walls — not a
 *  neighbours-only or depth-limited heuristic. Once the room closes, the exact
 *  polygon containment above takes over. */
function openChainViolation(p, fp, walls) {
  for (const w2 of walls) {
    if (w2.id === p.wallId || w2.type === "arc") continue;
    if ((w2.style || "standard") !== "standard") continue; // partitions are double-sided
    const info = getWallPlacementInfo(w2, walls);
    if (!info) continue;
    const rn = lhcRoomNormal(w2, walls) || { nx: info.nx, nz: info.nz };
    const T = effectiveThickness(w2);
    // signed distance from w2's INNER face (positive = interior side)
    const fx = (w2.start[0] + w2.end[0]) / 2 + rn.nx * (T / 2);
    const fz = (w2.start[1] + w2.end[1]) / 2 + rn.nz * (T / 2);
    for (const c of fp) {
      const along = (c[0] - w2.start[0]) * info.dirX + (c[1] - w2.start[1]) * info.dirZ;
      if (along < 0.001 || along > info.len - 0.001) continue; // outside this wall's run
      const d = (c[0] - fx) * rn.nx + (c[1] - fz) * rn.nz;
      if (d < -SHRINK_M) return "outside-room";
    }
  }
  return null;
}
